import React, { useState } from 'react';
import Markdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import type { Components } from 'react-markdown';
import ActionInfo from './ui/ActionInfo';
import remarkGfm from 'remark-gfm';
import WebSearchResults from './WebSearchResults';
import GridLayout from './search/GridLayout';

interface MessageProps {
  content: string | { type: string; data: any };
  role: 'user' | 'assistant' | 'system' | 'ui_component' | 'block_update' | 'reasoning';
  isCollapsed?: boolean;
  reasoning_type?: 'text' | 'summary';
  reasoning_duration?: number;  // If present, reasoning is complete
}

interface CodeComponentProps {
  node?: any;
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
  [key: string]: any;
}

interface MathComponentProps {
  value: string;
}

interface CustomComponents extends Components {
  math: React.ComponentType<MathComponentProps>;
  inlineMath: React.ComponentType<MathComponentProps>;
}

const Message: React.FC<MessageProps> = ({ content, role, isCollapsed = false, reasoning_type, reasoning_duration}) => {
  const [copied, setCopied] = useState(false);
  // Reasoning is streaming if no duration is set yet
  const isReasoningStreaming = reasoning_duration === undefined;
  // Auto-expand while streaming (no duration yet), collapse when done (has duration)
  const [isReasoningExpanded, setIsReasoningExpanded] = useState(isReasoningStreaming);
  const [hadNoDuration, setHadNoDuration] = useState(isReasoningStreaming);
  
  // Auto-collapse when duration becomes available (streaming finished)
  React.useEffect(() => {
    if (hadNoDuration && reasoning_duration !== undefined) {
      // Duration just became available, collapse the section
      setIsReasoningExpanded(false);
    }
    if (reasoning_duration === undefined && !hadNoDuration) {
      // New streaming started, expand the section
      setIsReasoningExpanded(true);
    }
    setHadNoDuration(reasoning_duration === undefined);
  }, [reasoning_duration, hadNoDuration]);

  const handleCopy = async (code: string) => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = (code: string, language: string) => {
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const extension = language === 'python' ? 'py' : language;
    a.download = `rhea.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const components: CustomComponents = {
    code({ node, inline, className, children, ...props }: CodeComponentProps) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      
      if (!inline && match) {
        return (
          <div className="relative group">
            <div className="absolute right-2 top-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => handleCopy(String(children))}
                className="px-2 py-1 text-xs bg-gray-700 rounded hover:bg-gray-600"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <button
                onClick={() => handleDownload(String(children), language)}
                className="px-2 py-1 text-xs bg-gray-700 rounded hover:bg-gray-600"
              >
                Download
              </button>
            </div>
            <SyntaxHighlighter
              style={vscDarkPlus as any}
              language={language}
              PreTag="div"
              {...props}
            >
              {String(children).replace(/\n$/, '')}
            </SyntaxHighlighter>
          </div>
        );
      }
      return <code className={className} {...props}>{children}</code>;
    },
    math: ({ value }: MathComponentProps) => (
      <span className="math math-display">
        {value}
      </span>
    ),
    inlineMath: ({ value }: MathComponentProps) => (
      <span className="math math-inline">
        {value}
      </span>
    )
  };

  const renderUIComponent = () => {
    if (role !== 'ui_component' || !content) return null;
    let contentObj;
    try {
      contentObj = typeof content === 'string' ? JSON.parse(content) : content;
    } catch (e) {
      console.error('Failed to parse UI component content:', e);
      return null;
    }
    const type = contentObj?.type;
    const data = contentObj?.data;
    if (!type || !data) return null;

    switch (type) {
      case 'search_engine_management':
        return <GridLayout items={data.results} singleRow={true}/>;
      case 'action_info':
        return <ActionInfo data={data} />;
      case 'web_search_results':
        return <WebSearchResults data={data} />;
      default:
        return null;
    }
  };

  // Render reasoning messages with collapsible section (subtle styling)
  if (role === 'reasoning') {
    const getHeaderText = () => {
      if (reasoning_duration !== undefined) {
        // Reasoning is complete - show duration
        return `Thought for ${reasoning_duration} second${reasoning_duration !== 1 ? 's' : ''}`;
      }
      // Still streaming
      return reasoning_type === 'summary' ? 'Reasoning Summary' : 'Thinking...';
    };

    return (
      <div className="flex items-start gap-2">
        <div className="inline-block max-w-[80%]">
          <button
            onClick={() => setIsReasoningExpanded(!isReasoningExpanded)}
            className="flex items-center gap-1 text-xs transition-colors hover:opacity-70"
            style={{
              color: 'var(--rhea-text-secondary)',
              opacity: 0.6
            }}
          >
            <span className="transform transition-transform text-[10px]" style={{ 
              transform: isReasoningExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
            }}>
              ▶
            </span>
            <span>
              {getHeaderText()}
            </span>
          </button>
          {isReasoningExpanded && (
            <div
              className="mt-1 text-xs overflow-auto max-h-96 pl-3"
              style={{
                color: 'var(--rhea-text-secondary)',
                opacity: 0.7
              }}
            >
              <Markdown
                remarkPlugins={[remarkMath, remarkGfm]}
                rehypePlugins={[rehypeKatex]}
                components={components}
              >
                {typeof content === 'string' ? content : ''}
              </Markdown>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex items-start gap-2 ${
        role === 'user' ? 'justify-end' : ''
      }`}
    >
      {role === 'assistant' && (
        <img src="/assets/rhea/logo.png" alt="Rhea" className="h-6 mt-1" />
      )}
      <div
        className={`p-3 rounded-lg ${
          isCollapsed ? 'max-h-24 overflow-hidden' : 'max-w-[80%]'
        }`}
        style={{ 
          backgroundColor: role === 'user' 
            ? 'var(--rhea-message-user)'
            : role === 'system'
            ? 'var(--rhea-message-system)'
            : 'var(--rhea-message-system)',
          color: 'var(--rhea-text-primary)'
        }}
      >
        {role === 'ui_component' ? (
          renderUIComponent()
        ) : (
          <Markdown
            remarkPlugins={[remarkMath, remarkGfm]}
            rehypePlugins={[rehypeKatex]}
            components={components}
          >
            {typeof content === 'string' ? content : ''}
          </Markdown>
        )}
      </div>
    </div>
  );
};

export default Message; 